import { makeObservable, observable, computed, action } from 'mobx';
import type {
    IdentityVerificationStatusResponseType,
    IdentityEkycVerificationProgressType,
    IdentityVerificationVariantType,
} from 'src/api_openapi/v2/idMission/schemas';
import type { Common } from 'src/AppState/Common';
import { apiVerificationsRequest } from 'src/api_openapi/v2/idMission/apiVerifications';
import { apiCancelTriggerRequest } from 'src/api_openapi/v2/idMission/apiCancelTrigger';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import showError from 'src/utils/error.jsx';

interface AuditIdMissionItemType {
    typeOfCheck: string;
    dateCreated: string;
    dateFinalized: string;
    internalStatus: IdentityEkycVerificationProgressType;
    statusMessage: string;
    requestId: string | number;
    traceId: string;
    ipAddress: string;
    verificationId: number;
}

interface AuditIdMissionJsonResponseType {
    responseStatus: 'success' | 'error';
    data: {
        jsonResponse: string;
        error?: {
            message: string;
        };
    };
}

const VERIFICATION_VARIANT_LABELS: Record<IdentityVerificationVariantType, string> = {
    IdCard: 'ID Card Verification',
    IdCardProofOfAddress: 'ID Card + Proof of Address',
    IdCardAndFace: 'ID Card + Face Match',
    IdCardAndFaceProofOfAddress: 'ID Card + Face + Proof of Address',
};

const mapVerificationVariantToType = (variant: IdentityVerificationVariantType): string => {
    return VERIFICATION_VARIANT_LABELS[variant];
};

export class AuditIdMissionState {
    private mapApiResponseToAuditData(
        verifications: IdentityVerificationStatusResponseType[]
    ): AuditIdMissionItemType[] {
        return verifications.map((verificationResponse) => {
            const { verification, status } = verificationResponse;

            return {
                typeOfCheck: mapVerificationVariantToType(verification.verificationVariant),
                dateCreated: verification.createdAt,
                dateFinalized: verification.finalizedAt ?? '--',
                internalStatus: verification.progress,
                statusMessage: status?.statusMessage ?? '--',
                requestId: status?.requestId ?? '--',
                traceId: status?.traceId ?? '--',
                ipAddress: status?.ipAddress ?? '--',
                verificationId: verification.id,
            };
        });
    }

    public readonly auditIdMissionResource: Resource<IdentityVerificationStatusResponseType[]>;
    @observable public selectedAuditJson: AuditIdMissionJsonResponseType | null = null;
    @observable public expandedRowId: string | null = null;
    @observable public cancelingVerificationId: number | null = null;

    public constructor(
        private readonly routerBackoffice: RouterBackoffice,
        private readonly common: Common
    ) {
        makeObservable(this);

        this.auditIdMissionResource = new Resource(async () => {
            const accountId = this.customerId;
            if (accountId === null) {
                throw new Error('No customer ID found');
            }

            const { gatewayUrl } = this.common.appEnvParams;
            const response = await apiVerificationsRequest(
                gatewayUrl,
                { account_id: accountId, latest_only: false },
                { authorization: `Bearer ${this.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                return response.body;
            } else if (response.status === 404) {
                return [];
            } else {
                throw new Error(`API returned status ${response.status}`);
            }
        });

        void this.auditIdMissionResource.refresh();
    }

    @computed private get customerId(): number | null {
        const customerNamespace = this.routerBackoffice.getValueString('customers');
        if (customerNamespace === null || typeof customerNamespace === 'number') {
            return typeof customerNamespace === 'number' ? customerNamespace : null;
        }
        const customerId = parseInt(customerNamespace, 10);
        return isNaN(customerId) ? null : customerId;
    }

    @computed public get status(): 'loading' | 'error' | 'noData' | 'success' {
        const result = this.auditIdMissionResource.get();
        if (result.type === 'loading') {
            return 'loading';
        }
        if (result.type === 'error') {
            return 'error';
        }
        if (result.value.length === 0) {
            return 'noData';
        }
        return 'success';
    }

    @computed public get auditData(): AuditIdMissionItemType[] {
        const result = this.auditIdMissionResource.get();
        if (result.type === 'ready') {
            return this.mapApiResponseToAuditData(result.value);
        }
        return [];
    }

    @computed private get originalVerifications(): IdentityVerificationStatusResponseType[] | null {
        const result = this.auditIdMissionResource.get();
        if (result.type === 'ready') {
            return result.value;
        }
        return null;
    }

    @action public getAuditIdMissionDataJson = (verificationId: number): void => {
        const rowKey = verificationId.toString();
        if (this.expandedRowId === rowKey) {
            this.expandedRowId = null;
            this.selectedAuditJson = null;
            return;
        }

        this.expandedRowId = rowKey;

        const auditItem = this.auditData.find((item) => item.verificationId === verificationId);
        if (auditItem == null) {
            this.selectedAuditJson = {
                responseStatus: 'error',
                data: {
                    jsonResponse: '',
                    error: {
                        message: 'Audit item not found',
                    },
                },
            };
            return;
        }

        const verification = this.auditData.find((item) => item.verificationId === verificationId);
        if (verification == null) {
            this.selectedAuditJson = {
                responseStatus: 'error',
                data: {
                    jsonResponse: '',
                    error: {
                        message: 'Verification not found',
                    },
                },
            };
            return;
        }

        const originalVerification = this.getCurrentVerificationData(verificationId);
        if (originalVerification != null && originalVerification.verification.rawResult != null) {
            try {
                const parsedRawResult =
                    typeof originalVerification.verification.rawResult === 'string'
                        ? JSON.parse(originalVerification.verification.rawResult)
                        : originalVerification.verification.rawResult;

                this.selectedAuditJson = {
                    responseStatus: 'success',
                    data: {
                        jsonResponse: JSON.stringify(parsedRawResult, null, 2),
                    },
                };
            } catch (parseError) {
                this.selectedAuditJson = {
                    responseStatus: 'success',
                    data: {
                        jsonResponse: JSON.stringify(
                            {
                                error: 'Failed to parse raw_result as JSON',
                                parseError: parseError instanceof Error ? parseError.message : String(parseError),
                                raw_result: originalVerification.verification.rawResult,
                            },
                            null,
                            2
                        ),
                    },
                };
            }
        } else {
            this.selectedAuditJson = {
                responseStatus: 'error',
                data: {
                    jsonResponse: '',
                    error: {
                        message: 'Raw result not available',
                    },
                },
            };
        }
    };

    private getCurrentVerificationData(verificationId: number): IdentityVerificationStatusResponseType | null {
        return this.originalVerifications?.find((v) => v.verification.id === verificationId) ?? null;
    }

    @computed public get isModalOpened(): boolean {
        const isOpened = this.routerBackoffice.getParametersString('customers', 'audits') === 'idMissionAudit';
        return isOpened;
    }

    @action public cancelVerification = async (verificationId: number): Promise<void> => {
        const accountId = this.customerId;
        if (accountId === null) {
            showError('No customer ID found');
            return;
        }

        this.cancelingVerificationId = verificationId;

        try {
            const { gatewayUrl } = this.common.appEnvParams;
            const response = await apiCancelTriggerRequest(
                gatewayUrl,
                {
                    verification_id: verificationId,
                    requestBody: { accountId },
                },
                { authorization: `Bearer ${this.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                await this.auditIdMissionResource.refresh();
            } else if (response.status === 404) {
                showError('Verification not found');
            } else if (response.status === 400) {
                showError(`Bad request: ${response.body}`);
            } else {
                showError('Failed to cancel verification');
            }
        } catch (error) {
            showError('Error cancelling verification');
        } finally {
            this.cancelingVerificationId = null;
        }
    };
}
